/**
 * PanelContent
 *
 * Renders the content for a split view panel based on its type and route.
 * Each panel can display any page of the application.
 *
 * IMPORTANT: All lazy imports MUST be declared at module level (outside components)
 * to prevent re-creation on every render, which would cause content to reload
 * on focus changes or resizes.
 */

import React, { Suspense, lazy, useMemo, memo } from "react";
import { SplitPanel } from "@/hooks/use-tabs-store";
import { PanelRouteProvider } from "@/hooks/use-panel-route";

// ============================================================================
// Lazy-loaded Page Components
// ============================================================================
// IMPORTANT: These MUST be at module level to prevent re-creation on render

// Main pages
const HomePage = lazy(() => import("@/pages/index"));
const DashboardsPage = lazy(() => import("@/pages/dashboards/index"));
const ChatPage = lazy(() => import("@/pages/chat/index"));
const SearchPage = lazy(() => import("@/pages/search"));
const TrackersPage = lazy(() => import("@/pages/trackers/index"));
const EnrichmentsPage = lazy(() => import("@/pages/enrichments/index"));
const AlertsPage = lazy(() => import("@/pages/alerts/index"));
const SettingsPage = lazy(() => import("@/pages/settings/index"));
const WidgetsPage = lazy(() => import("@/pages/widgets/index"));
const BranchesPage = lazy(() => import("@/pages/branches/index"));
const InvestigationsPage = lazy(() => import("@/pages/investigations/index"));
const ReportsPage = lazy(() => import("@/pages/reports/index"));
const WorkspacesPage = lazy(() => import("@/pages/workspaces/index"));
const TokensPage = lazy(() => import("@/pages/tokens/index"));
const MembersPage = lazy(() => import("@/pages/members/index"));
const ChannelsPage = lazy(() => import("@/pages/channels/index"));
const OrganizationPage = lazy(() => import("@/pages/organization/index"));

// ============================================================================
// Types
// ============================================================================

interface PanelContentProps {
  /** The panel configuration */
  panel: SplitPanel;
  /** Whether this panel is currently focused */
  isFocused?: boolean;
}

// ============================================================================
// Loading Component
// ============================================================================

const PanelLoader: React.FC = () => (
  <div className="flex items-center justify-center h-full bg-surface">
    <div className="animate-spin w-8 h-8 border-4 border-[var(--arc-accent)] border-t-transparent rounded-full" />
  </div>
);

// ============================================================================
// Panel Content Wrapper
// ============================================================================

/**
 * Wrapper that provides isolated route context for each panel.
 * This ensures that pages inside the panel use the panel's route
 * instead of the browser URL when calling usePanelSearchParams().
 */
const PanelContentWrapper: React.FC<{
  children: React.ReactNode;
  panel: SplitPanel;
}> = ({ children, panel }) => {
  return (
    <PanelRouteProvider route={panel.route}>
      {/* @container enables container queries for adaptive layouts within each panel */}
      <div
        className="@container h-full w-full overflow-hidden"
        data-panel-id={panel.id}
      >
        {children}
      </div>
    </PanelRouteProvider>
  );
};

// ============================================================================
// Content Renderer
// ============================================================================

/**
 * Renders the appropriate content based on panel type.
 * Memoized to prevent re-renders when only focus changes.
 * Note: We intentionally ignore isFocused in the comparison since it shouldn't
 * trigger a re-render of the content itself.
 */
const PanelContent: React.FC<PanelContentProps> = memo(
  function PanelContent({ panel }) {
    // Parse route to extract params
    const searchParams = useMemo(() => {
      try {
        const url = new URL(panel.route, window.location.origin);
        return url.searchParams;
      } catch {
        return new URLSearchParams();
      }
    }, [panel.route]);

    // Get the ID param if present
    const id = searchParams.get("id") || panel.resourceId;

    // Memoize content to prevent re-renders
    const content = useMemo(() => {
      switch (panel.type) {
        case "dashboard":
          return <DashboardPanelContent dashboardId={id} />;

        case "tracker":
        case "trackers":
          return <TrackersPanelContent trackerId={id} />;

        case "alert":
        case "alerts":
          return <AlertsPanelContent alertId={id} />;

        case "enrichment":
        case "enrichments":
          return <TrackersPanelContent trackerId={id} tab="enrichments" />;

        case "chat":
          return <ChatPanelContent conversationId={id} />;

        case "search":
          return <SearchPanelContent />;

        case "home":
          return <HomePanelContent />;

        case "account":
          return <AccountPanelContent />;

        case "widgets":
          return <WidgetsPanelContent />;

        case "branches":
          return <BranchesPanelContent />;

        case "investigation":
        case "investigations":
          return <InvestigationsPanelContent investigationId={id} />;

        case "reports":
          return <ReportsPanelContent reportId={id} />;

        case "workspaces":
          return <WorkspacesPanelContent />;

        case "tokens":
          return <TokensPanelContent />;

        case "members":
          return <MembersPanelContent />;

        case "channels":
          return <ChannelsPanelContent />;

        case "organization":
          return <OrganizationPanelContent />;

        case "settings":
          return <AccountPanelContent />;

        default:
          return (
            <div className="flex items-center justify-center h-full text-content-secondary">
              Unknown panel type: {panel.type}
            </div>
          );
      }
    }, [panel.type, id]);

    return (
      <PanelContentWrapper panel={panel}>
        <Suspense fallback={<PanelLoader />}>{content}</Suspense>
      </PanelContentWrapper>
    );
  },
  // Custom comparison: only re-render if panel.id, panel.type, or panel.route changes
  // Ignore isFocused changes since focus is visual only (handled by parent border)
  (prevProps, nextProps) => {
    return (
      prevProps.panel.id === nextProps.panel.id &&
      prevProps.panel.type === nextProps.panel.type &&
      prevProps.panel.route === nextProps.panel.route &&
      prevProps.panel.resourceId === nextProps.panel.resourceId
    );
  },
);

// ============================================================================
// Panel-specific Content Components
// ============================================================================
// All these components are memoized to prevent unnecessary re-renders

/**
 * Dashboard panel content.
 * Renders a single dashboard or the gallery.
 */
const DashboardPanelContent = memo<{
  dashboardId?: string | null;
  isFocused?: boolean;
}>(function DashboardPanelContent({ dashboardId, isFocused }) {
  return (
    <Suspense fallback={<PanelLoader />}>
      <DashboardsPage />
    </Suspense>
  );
});

/**
 * Trackers panel content.
 */
const TrackersPanelContent = memo<{
  trackerId?: string | null;
  tab?: "trackers" | "enrichments";
  isFocused?: boolean;
}>(function TrackersPanelContent({ trackerId, tab = "trackers", isFocused }) {
  // Use the appropriate page based on tab
  if (tab === "enrichments") {
    return (
      <Suspense fallback={<PanelLoader />}>
        <EnrichmentsPage />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<PanelLoader />}>
      <TrackersPage />
    </Suspense>
  );
});

/**
 * Alerts panel content.
 */
const AlertsPanelContent = memo<{
  alertId?: string | null;
  isFocused?: boolean;
}>(function AlertsPanelContent({ alertId, isFocused }) {
  return (
    <Suspense fallback={<PanelLoader />}>
      <AlertsPage />
    </Suspense>
  );
});

/**
 * Chat panel content.
 */
const ChatPanelContent = memo<{
  conversationId?: string | null;
  isFocused?: boolean;
}>(function ChatPanelContent({ conversationId, isFocused }) {
  return (
    <Suspense fallback={<PanelLoader />}>
      <ChatPage />
    </Suspense>
  );
});

/**
 * Search panel content.
 */
const SearchPanelContent = memo<{
  isFocused?: boolean;
}>(function SearchPanelContent({ isFocused }) {
  return (
    <Suspense fallback={<PanelLoader />}>
      <SearchPage />
    </Suspense>
  );
});

/**
 * Home panel content.
 */
const HomePanelContent = memo<{
  isFocused?: boolean;
}>(function HomePanelContent({ isFocused }) {
  return (
    <Suspense fallback={<PanelLoader />}>
      <HomePage />
    </Suspense>
  );
});

/**
 * Account panel content.
 */
const AccountPanelContent = memo<{
  isFocused?: boolean;
}>(function AccountPanelContent({ isFocused }) {
  return (
    <Suspense fallback={<PanelLoader />}>
      <SettingsPage />
    </Suspense>
  );
});

/**
 * Widgets gallery panel content.
 */
const WidgetsPanelContent = memo<{
  isFocused?: boolean;
}>(function WidgetsPanelContent({ isFocused }) {
  return (
    <Suspense fallback={<PanelLoader />}>
      <WidgetsPage />
    </Suspense>
  );
});

/**
 * Branches panel content.
 */
const BranchesPanelContent = memo<{
  isFocused?: boolean;
}>(function BranchesPanelContent({ isFocused }) {
  return (
    <Suspense fallback={<PanelLoader />}>
      <BranchesPage />
    </Suspense>
  );
});

/**
 * Investigations panel content.
 */
const InvestigationsPanelContent = memo<{
  investigationId?: string | null;
  isFocused?: boolean;
}>(function InvestigationsPanelContent({ investigationId, isFocused }) {
  return (
    <Suspense fallback={<PanelLoader />}>
      <InvestigationsPage />
    </Suspense>
  );
});

/**
 * Reports panel content.
 */
const ReportsPanelContent = memo<{
  reportId?: string | null;
  isFocused?: boolean;
}>(function ReportsPanelContent({ reportId, isFocused }) {
  return (
    <Suspense fallback={<PanelLoader />}>
      <ReportsPage />
    </Suspense>
  );
});

/**
 * Workspaces panel content.
 */
const WorkspacesPanelContent = memo<{
  isFocused?: boolean;
}>(function WorkspacesPanelContent({ isFocused }) {
  return (
    <Suspense fallback={<PanelLoader />}>
      <WorkspacesPage />
    </Suspense>
  );
});

/**
 * Tokens panel content.
 */
const TokensPanelContent = memo<{
  isFocused?: boolean;
}>(function TokensPanelContent({ isFocused }) {
  return (
    <Suspense fallback={<PanelLoader />}>
      <TokensPage />
    </Suspense>
  );
});

/**
 * Members panel content.
 */
const MembersPanelContent = memo<{
  isFocused?: boolean;
}>(function MembersPanelContent({ isFocused }) {
  return (
    <Suspense fallback={<PanelLoader />}>
      <MembersPage />
    </Suspense>
  );
});

/**
 * Channels panel content.
 */
const ChannelsPanelContent = memo<{
  isFocused?: boolean;
}>(function ChannelsPanelContent({ isFocused }) {
  return (
    <Suspense fallback={<PanelLoader />}>
      <ChannelsPage />
    </Suspense>
  );
});

/**
 * Organization panel content.
 */
const OrganizationPanelContent = memo<{
  isFocused?: boolean;
}>(function OrganizationPanelContent({ isFocused }) {
  return (
    <Suspense fallback={<PanelLoader />}>
      <OrganizationPage />
    </Suspense>
  );
});

export default PanelContent;
